import { useEffect, useRef, useCallback } from 'react';

const APP_VERSION_CHECK_INTERVAL = 15000; // Check every 15 seconds (more aggressive)

// Clear browser caches but PRESERVE session/auth data
const clearAppCaches = async () => {
  console.log('[AutoUpdate] Clearing caches (preserving session)...');
  
  // Clear all caches
  if ('caches' in window) {
    try {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(name => caches.delete(name)));
      console.log('[AutoUpdate] Caches cleared:', cacheNames.length);
    } catch (e) {
      console.log('[AutoUpdate] Cache clear error:', e);
    }
  }

  // Unregister ALL service workers
  if ('serviceWorker' in navigator) {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(reg => reg.unregister()));
      console.log('[AutoUpdate] Service workers unregistered:', registrations.length);
    } catch (e) {
      console.log('[AutoUpdate] SW unregister error:', e);
    }
  }
  
  console.log('[AutoUpdate] Caches cleared (session preserved)');
};

export const useAutoUpdate = () => {
  const currentVersion = useRef(null);
  const isChecking = useRef(false);

  const checkForUpdates = useCallback(async () => {
    if (isChecking.current) return;
    isChecking.current = true;

    try {
      // Fetch version with aggressive cache-busting
      const response = await fetch(`/version.json?nocache=${Date.now()}&r=${Math.random()}`, {
        cache: 'no-store',
        method: 'GET',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate, max-age=0',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      
      if (!response.ok) {
        isChecking.current = false;
        return;
      }

      const data = await response.json();
      const newVersion = data.version;

      // First load - store version and clear any stale data
      if (currentVersion.current === null) {
        currentVersion.current = newVersion;
        console.log('[AutoUpdate] Initial version:', newVersion);
        
        // On first load, always clear service workers (they cause issues)
        if ('serviceWorker' in navigator) {
          const registrations = await navigator.serviceWorker.getRegistrations();
          if (registrations.length > 0) {
            console.log('[AutoUpdate] Clearing stale service workers on startup');
            await Promise.all(registrations.map(reg => reg.unregister()));
          }
        }
        
        isChecking.current = false;
        return;
      }

      // Check if version changed
      if (currentVersion.current !== newVersion) {
        console.log('[AutoUpdate] NEW VERSION DETECTED:', newVersion, 'old:', currentVersion.current);
        console.log('[AutoUpdate] Forcing full reload...');
        
        // Clear caches before reload (but NOT session data)
        await clearAppCaches();

        // Force hard reload from server
        window.location.href = window.location.origin + window.location.pathname + '?v=' + newVersion;
        return;
      }
    } catch (error) {
      console.log('[AutoUpdate] Check failed:', error.message);
    }

    isChecking.current = false;
  }, []);

  useEffect(() => {
    // Check immediately on mount
    checkForUpdates();

    // Check periodically (every 15 seconds)
    const interval = setInterval(checkForUpdates, APP_VERSION_CHECK_INTERVAL);

    // Check when tab becomes visible
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setTimeout(checkForUpdates, 500); // Small delay to ensure network is ready
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Check when window gains focus
    const handleFocus = () => setTimeout(checkForUpdates, 500);
    window.addEventListener('focus', handleFocus);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [checkForUpdates]);
};

export default useAutoUpdate;
